import { useEffect, useMemo, useState } from 'react'
import type { LoginResponse } from '../api/auth'
import { CUSTOMER_SEGMENT_OPTIONS } from '../api/contracts'
import { fetchAllProducts, fetchDepartments, fetchOperators } from '../api/catalog'
import { CloneConfigurationButton, useOpenAfterClone } from '../components/CloneConfigurationButton'
import { confirmActivation } from '../api/configurationCheck'
import {
  Page,
  PageHeader,
  FilterBar,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  Checkbox,
  type PillTone,
  FormSection,
  FormGrid,
} from '../components/ui'
import type { Department, Operator, Product } from '../api/catalog'
import {
  CONDITION_OPERATORS,
  CONTACT_FORBIDDEN_TYPES,
  CONTRACT_ATTRIBUTES,
  CUSTOMER_PREFILL_ATTRIBUTES,
  DYNAMIC_FIELD_TARGETS,
  DYNAMIC_FIELD_TYPES,
  MAX_PREFILL_SOURCES,
  dynamicFieldsApi,
  prefillSourceLabel,
  type ConditionOperator,
  type DisplayCondition,
  type DynamicFieldBody,
  type DynamicFieldDefinition,
  type DynamicFieldStatus,
  type DynamicFieldTarget,
  type DynamicFieldType,
  type PrefillQuestionChoice,
  type PrefillSource,
} from '../api/dynamicFields'
import { defaultToText, supportsDefault, textToDefault } from '../lib/dynamicFieldRules'

/**
 * F-074 (TASK-0328, REQ-042-001/002/003): campi dinamici della pratica. L'Admin crea i campi senza
 * programmatore: tipo, obbligatorieta', ambito (settore, operatore, prodotto, segmento), condizioni di
 * visualizzazione, validazione e formula dei campi calcolati. Una definizione si scrive in Bozza, si attiva
 * (da quel momento non si modifica piu') e si fa evolvere con una nuova versione. L'etichetta "mapping
 * portale" e' solo informativa: non esiste un collegamento reale con i portali degli operatori.
 * F-098 (TASK-0352): "Origine del valore" - da quali domande dei questionari o attributi dell'anagrafica si
 * precompila il campo (elenco ordinato, vince la prima origine che da' un valore). Senza origini vale
 * l'abbinamento per codice uguale. Le origini si modificano solo in Bozza.
 * F-108 (TASK-0362, REQ-004-003): selettore "Destinazione: Pratica / Cliente". I campi del cliente compaiono
 * nella scheda del cliente (`/contacts/:id`): niente ambito di pratica, formula, origine del valore, tipi
 * Importo e Calcolato. La destinazione si sceglie alla creazione e non cambia piu'.
 */
const STATUS_TONE: Record<DynamicFieldStatus, PillTone> = {
  Bozza: 'neutral',
  Attivo: 'success',
  Archiviato: 'neutral',
}

const NUMERIC_TYPES: DynamicFieldType[] = ['Numero', 'Importo', 'Percentuale']

function isAdminRole(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

interface FormState {
  id?: number
  target: DynamicFieldTarget
  code: string
  label: string
  section: string
  sortOrder: string
  type: DynamicFieldType
  required: boolean
  departmentId: string
  operatorId: string
  productId: string
  customerSegment: string
  optionsText: string
  min: string
  max: string
  maxLength: string
  decimals: string
  conditions: DisplayCondition[]
  formula: string
  externalMappingLabel: string
  sources: PrefillSource[]
  /** R3-12: valore predefinito come testo (vedi lib/dynamicFieldRules). */
  defaultText: string
  changeReason: string
}

const EMPTY_FORM: FormState = {
  target: 'Contract',
  code: '',
  label: '',
  section: '',
  sortOrder: '0',
  type: 'Testo',
  required: false,
  departmentId: '',
  operatorId: '',
  productId: '',
  customerSegment: '',
  optionsText: '',
  min: '',
  max: '',
  maxLength: '',
  decimals: '',
  conditions: [],
  formula: '',
  externalMappingLabel: '',
  sources: [],
  defaultText: '',
  changeReason: '',
}

const MAX_CONDITIONS = 5

function toForm(d: DynamicFieldDefinition): FormState {
  return {
    id: d.id,
    target: d.target ?? 'Contract',
    code: d.code,
    label: d.label,
    section: d.section ?? '',
    sortOrder: String(d.sortOrder),
    type: d.type,
    required: d.required,
    departmentId: d.departmentId === null ? '' : String(d.departmentId),
    operatorId: d.operatorId === null ? '' : String(d.operatorId),
    productId: d.productId === null ? '' : String(d.productId),
    customerSegment: d.customerSegment ?? '',
    optionsText: d.options.join('\n'),
    min: d.validation?.min === undefined ? '' : String(d.validation.min),
    max: d.validation?.max === undefined ? '' : String(d.validation.max),
    maxLength: d.validation?.maxLength === undefined ? '' : String(d.validation.maxLength),
    decimals: d.validation?.decimals === undefined ? '' : String(d.validation.decimals),
    conditions: d.displayCondition ?? [],
    formula: d.formula ?? '',
    externalMappingLabel: d.externalMappingLabel ?? '',
    sources: d.prefillSources ?? [],
    defaultText: defaultToText(d.type, d.defaultValue),
    changeReason: d.changeReason ?? '',
  }
}

function toBody(f: FormState): DynamicFieldBody {
  const num = (s: string) => (s.trim() === '' ? undefined : Number(s))
  const validation: Record<string, number> = {}
  for (const [k, raw] of [['min', f.min], ['max', f.max], ['maxLength', f.maxLength], ['decimals', f.decimals]] as const) {
    const n = num(raw)
    if (n !== undefined) validation[k] = n
  }
  const isList = f.type === 'Elenco' || f.type === 'ElencoMultiplo'
  // F-108: un campo del cliente non ha ambito di pratica, formula né origini.
  const forContact = f.target === 'Contact'
  return {
    ...(f.id === undefined ? { code: f.code.trim(), target: f.target } : {}),
    label: f.label.trim(),
    type: f.type,
    required: f.type === 'Calcolato' ? false : f.required,
    section: f.section.trim() || null,
    sortOrder: Number(f.sortOrder) || 0,
    departmentId: !forContact && f.departmentId ? Number(f.departmentId) : null,
    operatorId: !forContact && f.operatorId ? Number(f.operatorId) : null,
    productId: !forContact && f.productId ? Number(f.productId) : null,
    customerSegment: !forContact && f.customerSegment ? f.customerSegment : null,
    options: isList ? f.optionsText.split('\n').map(o => o.trim()).filter(o => o !== '') : null,
    validation: Object.keys(validation).length > 0 ? validation : null,
    displayCondition: f.conditions.filter(c => c.field !== '').length > 0 ? f.conditions.filter(c => c.field !== '') : null,
    formula: !forContact && f.type === 'Calcolato' ? f.formula.trim() : null,
    externalMappingLabel: f.externalMappingLabel.trim() || null,
    prefillSources: forContact || f.type === 'Calcolato' ? null : cleanSources(f.sources),
    defaultValue: textToDefault(f.type, f.defaultText),
    changeReason: f.changeReason.trim() || null,
  }
}

/** Le origini compilate, senza righe vuote; nessuna origine = `null`. */
function cleanSources(list: PrefillSource[]): PrefillSource[] | null {
  const out = list
    .map((s): PrefillSource => (s.kind === 'question' ? { kind: 'question', code: s.code.trim() } : s))
    .filter(s => (s.kind === 'question' ? s.code !== '' : s.attribute !== ''))
  return out.length > 0 ? out : null
}

function scopeText(d: DynamicFieldDefinition, deps: Department[], ops: Operator[], prods: Product[]): string {
  const parts: string[] = []
  if (d.departmentId !== null) parts.push(`Settore: ${deps.find(x => x.id === d.departmentId)?.name ?? `#${d.departmentId}`}`)
  if (d.operatorId !== null) parts.push(`Operatore: ${ops.find(x => x.id === d.operatorId)?.name ?? `#${d.operatorId}`}`)
  if (d.productId !== null) parts.push(`Prodotto: ${prods.find(x => x.id === d.productId)?.name ?? `#${d.productId}`}`)
  if (d.customerSegment) parts.push(`Segmento: ${d.customerSegment}`)
  if (d.target === 'Contact') return 'Tutti i clienti'
  return parts.length === 0 ? 'Tutte le pratiche' : parts.join(' · ')
}

export function DynamicFieldsPage({ auth }: { auth: LoginResponse }) {
  const canWrite = isAdminRole(auth)
  const [items, setItems] = useState<DynamicFieldDefinition[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<DynamicFieldStatus | ''>('')
  // F-108: destinazione mostrata (Pratica o Cliente). Alla creazione il nuovo campo nasce con questa.
  const [target, setTarget] = useState<DynamicFieldTarget>('Contract')
  const [departments, setDepartments] = useState<Department[]>([])
  const [operators, setOperators] = useState<Operator[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [questions, setQuestions] = useState<PrefillQuestionChoice[]>([])

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)

  const [versionTarget, setVersionTarget] = useState<DynamicFieldDefinition | null>(null)
  const [versionReason, setVersionReason] = useState('')
  const [versionError, setVersionError] = useState<string | null>(null)

  const isEdit = form.id !== undefined
  const isList = form.type === 'Elenco' || form.type === 'ElencoMultiplo'
  const isNumeric = NUMERIC_TYPES.includes(form.type)
  const isContactForm = form.target === 'Contact'
  const typeChoices = isContactForm
    ? DYNAMIC_FIELD_TYPES.filter(t => !CONTACT_FORBIDDEN_TYPES.includes(t.value))
    : DYNAMIC_FIELD_TYPES

  async function load() {
    setLoading(true)
    setError(null)
    try {
      setItems(await dynamicFieldsApi.listAll(undefined, target))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target])

  useEffect(() => {
    fetchDepartments().then(setDepartments).catch(() => setDepartments([]))
    fetchOperators().then(r => setOperators(r.items)).catch(() => setOperators([]))
    fetchAllProducts().then(r => setProducts(r.items)).catch(() => setProducts([]))
    void dynamicFieldsApi.prefillQuestions().then(setQuestions)
  }, [])

  const visible = useMemo(
    () => items.filter(i => statusFilter === '' || i.status === statusFilter),
    [items, statusFilter],
  )

  // Campi citabili nelle condizioni: attributi della pratica e codici degli altri campi.
  const conditionFieldChoices = useMemo(() => {
    const codes = [...new Set(items.map(i => i.code))].filter(c => c !== form.code)
    return [
      // F-108: un campo del cliente puo' citare solo altri campi del cliente.
      ...(form.target === 'Contact' ? [] : CONTRACT_ATTRIBUTES),
      ...codes.map(c => ({ value: c, label: `Campo: ${items.find(i => i.code === c)?.label ?? c} (${c})` })),
    ]
  }, [items, form.code, form.target])

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(f => ({ ...f, [key]: value }))
  }

  function openCreate() {
    setForm({ ...EMPTY_FORM, target })
    setModalError(null)
    setShowModal(true)
  }

  function openEdit(d: DynamicFieldDefinition) {
    setForm(toForm(d))
    setModalError(null)
    setShowModal(true)
  }

  // F-100: dopo "Clona" la copia (Bozza v1) compare nell'elenco e si apre in modifica.
  const [cloneNotice, setCloneNotice] = useState('')
  const openAfterClone = useOpenAfterClone(items, d => openEdit(d))

  async function handleSave() {
    if (!isEdit && !form.code.trim()) return setModalError('Il codice e\' obbligatorio')
    if (!form.label.trim()) return setModalError('L\'etichetta e\' obbligatoria')
    if (isList && form.optionsText.trim() === '') return setModalError('Indica le opzioni dell\'elenco, una per riga')
    if (form.type === 'Calcolato' && !form.formula.trim()) return setModalError('Indica la formula del campo calcolato')
    setSaving(true)
    setModalError(null)
    try {
      const body = toBody(form)
      if (isEdit) await dynamicFieldsApi.update(form.id as number, body)
      else await dynamicFieldsApi.create(body)
      setShowModal(false)
      await load()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function run(action: () => Promise<unknown>) {
    setError(null)
    try {
      await action()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nell\'operazione')
    }
  }

  async function handleNewVersion() {
    if (!versionTarget) return
    if (!versionReason.trim()) return setVersionError('La motivazione e\' obbligatoria')
    setVersionError(null)
    try {
      await dynamicFieldsApi.newVersion(versionTarget.id, versionReason.trim())
      setVersionTarget(null)
      setVersionReason('')
      await load()
    } catch (e) {
      setVersionError(e instanceof Error ? e.message : 'Errore nella creazione della versione')
    }
  }

  function updateSource(i: number, next: PrefillSource) {
    setForm(f => ({ ...f, sources: f.sources.map((s, idx) => (idx === i ? next : s)) }))
  }

  function moveSource(i: number, delta: -1 | 1) {
    setForm(f => {
      const j = i + delta
      if (j < 0 || j >= f.sources.length) return f
      const next = [...f.sources]
      ;[next[i], next[j]] = [next[j], next[i]]
      return { ...f, sources: next }
    })
  }

  function updateCondition(i: number, patch: Partial<DisplayCondition>) {
    setForm(f => ({ ...f, conditions: f.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }))
  }

  if (loading) {
    return (
      <Page>
        <LoadingState />
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title={target === 'Contact' ? 'Campi aggiuntivi del cliente' : 'Campi della pratica'}
        icon="input"
        back={{ to: '/settings', label: 'Impostazioni generali' }}
        actions={
          canWrite ? (
            <Button variant="filled" icon="add" onClick={openCreate}>
              Nuovo campo
            </Button>
          ) : undefined
        }
      />

      <Callout tone="info">
        {
            target === 'Contact' ? (
              <>
                Campi aggiuntivi dell&apos;anagrafica del cliente: compaiono nella scheda del cliente (&quot;Campi
                aggiuntivi&quot;) e non su nessuna pratica. Non hanno ambito, formula o origine del valore e non
                ammettono i tipi Importo e Calcolato. Una definizione attiva non si modifica: se ne crea una nuova
                versione. Un campo obbligatorio visibile e vuoto impedisce di salvare i campi del cliente.
              </>
            ) : (
              <>
                Campi aggiuntivi che compaiono nella &quot;Nuova pratica&quot; e nel dettaglio in base a settore, operatore,
                prodotto e segmento. Una definizione attiva non si modifica: se ne crea una nuova versione, e i valori
                gia&apos; salvati conservano la versione con cui sono stati scritti. Al momento dell&apos;invio al
                backoffice un campo obbligatorio mancante viene segnalato.
              </>
            )
          }
      </Callout>

      {error && <Callout tone="error">{error}</Callout>}
      {cloneNotice && (
        <Callout
          tone="info"
          action={<IconButton icon="close" size="sm" aria-label="Chiudi" onClick={() => setCloneNotice('')} />}
        >
          {cloneNotice}
        </Callout>
      )}

      <FilterBar>
        <Select
          id="df-target"
          label="Destinazione"
          value={target}
          onChange={e => setTarget(e.target.value as DynamicFieldTarget)}
        >
          {DYNAMIC_FIELD_TARGETS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
        <Select
          id="df-status"
          label="Stato"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as DynamicFieldStatus | '')}
        >
          <option value="">Tutti</option>
          <option value="Bozza">Bozza</option>
          <option value="Attivo">Attivo</option>
          <option value="Archiviato">Archiviato</option>
        </Select>
      </FilterBar>

      <Card variant="outlined" padding="none">
        {visible.length === 0 ? (
          <EmptyState icon="input" title="Nessun campo configurato." />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Codice</th>
                <th>Etichetta</th>
                <th>Tipo</th>
                <th>Ambito</th>
                <th>Obbligatorio</th>
                {target === 'Contract' && <th>Origine</th>}
                <th>Versione</th>
                <th>Stato</th>
                {canWrite && <th>Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {visible.map(d => (
                <tr key={d.id}>
                  <td className="md-typescale-emphasized-body-medium">{d.code}</td>
                  <td>
                    {d.label}
                    {d.displayCondition && d.displayCondition.length > 0 && (
                      <StatusPill tone="neutral" className="ms-2" title="Ha condizioni di visualizzazione">
                        condizionale
                      </StatusPill>
                    )}
                  </td>
                  <td>{DYNAMIC_FIELD_TYPES.find(t => t.value === d.type)?.label ?? d.type}</td>
                  <td>{scopeText(d, departments, operators, products)}</td>
                  <td>{d.required ? 'Si' : 'No'}</td>
                  {target === 'Contract' && (
                    <td title={d.prefillSources?.map(prefillSourceLabel).join(' > ') ?? 'Abbinamento per codice uguale'}>
                      {d.prefillSources && d.prefillSources.length > 0 ? `${d.prefillSources.length} configurate` : 'per codice'}
                    </td>
                  )}
                  <td title={d.changeReason ?? undefined} className="md-num">v{d.version}</td>
                  <td>
                    <StatusPill tone={STATUS_TONE[d.status]}>{d.status}</StatusPill>
                  </td>
                  {canWrite && (
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        <CloneConfigurationButton
                          entityLabel="campo dinamico"
                          url={`/api/dynamic-fields/${d.id}/clone`}
                          defaultName={d.label}
                          extraFields={[{ key: 'code', label: 'Codice della copia', required: true, placeholder: 'es. pod_gas' }]}
                          note="Si copiano tipo, ambito, opzioni, condizioni, formula e precompilazione. La copia nasce in bozza, versione 1, con il nuovo codice."
                          onCloned={async id => { await load(); setCloneNotice('Copia creata: è inattiva (bozza v1). Controllala e attivala quando è pronta.'); openAfterClone(id) }}
                        />
                        {d.status === 'Bozza' && (
                          <>
                            <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(d)} />
                            <IconButton icon="check_circle" size="sm" aria-label="Attiva" onClick={() => void run(async () => { if (await confirmActivation('DynamicField', d.id, d.label)) await dynamicFieldsApi.activate(d.id) })} />
                          </>
                        )}
                        {d.status !== 'Bozza' && (
                          <IconButton
                            icon="content_copy"
                            size="sm"
                            aria-label="Nuova versione"
                            onClick={() => {
                              setVersionTarget(d)
                              setVersionReason('')
                              setVersionError(null)
                            }}
                          />
                        )}
                        {d.status !== 'Archiviato' && (
                          <IconButton icon="archive" size="sm" aria-label="Archivia" onClick={() => void run(() => dynamicFieldsApi.archive(d.id))} />
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={() => setShowModal(false)}
        dismissible={!saving}
        title={isEdit ? `Modifica campo (bozza v${items.find(i => i.id === form.id)?.version ?? ''})` : 'Nuovo campo'}
        actions={
          <>
            <Button variant="text" onClick={() => setShowModal(false)} disabled={saving}>Annulla</Button>
            <Button type="submit" variant="text" disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva in bozza'}
            </Button>
          </>
        }
      >
        <>
          {modalError && <Callout tone="error">{modalError}</Callout>}
          <div>
            <StatusPill tone="neutral">
              {DYNAMIC_FIELD_TARGETS.find(t => t.value === form.target)?.label}
            </StatusPill>
          </div>
          <FormSection title="Campo">
            <TextField id="dfm-code" label="Codice" value={form.code} disabled={isEdit} maxLength={50}
              supportingText="es. durata_vincolo. a-z, 0-9 e _ (non modificabile)."
              onChange={e => setField('code', e.target.value.toLowerCase())} />
            <TextField id="dfm-label" label="Etichetta" value={form.label} maxLength={150} onChange={e => setField('label', e.target.value)} />
            <Select id="dfm-type" label="Tipo" value={form.type} onChange={e => setField('type', e.target.value as DynamicFieldType)}>
              {typeChoices.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
            <TextField id="dfm-section" label="Sezione" value={form.section} maxLength={60} onChange={e => setField('section', e.target.value)} />
            <TextField id="dfm-sort" label="Ordine" type="number" value={form.sortOrder} onChange={e => setField('sortOrder', e.target.value)} />
          </FormSection>

          {!isContactForm && (
            <FormSection title="Ambito" description="Il campo compare solo sulle pratiche con questi valori. Vuoto = tutte.">
              <Select id="dfm-dep" label="Settore" value={form.departmentId} onChange={e => setField('departmentId', e.target.value)}>
                <option value="">Tutti</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Select>
              <Select id="dfm-op" label="Operatore" value={form.operatorId} onChange={e => setField('operatorId', e.target.value)}>
                <option value="">Tutti</option>
                {operators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </Select>
              <Select id="dfm-prod" label="Prodotto" value={form.productId} onChange={e => setField('productId', e.target.value)}>
                <option value="">Tutti</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
              <Select id="dfm-seg" label="Segmento cliente" value={form.customerSegment} onChange={e => setField('customerSegment', e.target.value)}>
                <option value="">Tutti</option>
                {CUSTOMER_SEGMENT_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </FormSection>
          )}

          {isList && (
            <FormSection title="Scelte dell'elenco" columns={1}>
              <TextField id="dfm-options" label="Opzioni (una per riga)" multiline rows={4} value={form.optionsText} onChange={e => setField('optionsText', e.target.value)} />
            </FormSection>
          )}

          {!isContactForm && form.type === 'Calcolato' && (
            <FormSection title="Calcolo" columns={1}>
              <TextField id="dfm-formula" label="Formula" value={form.formula} maxLength={500}
                supportingText="es. canone * mesi. Solo + - * / e parentesi, con i codici di altri campi numerici (Numero, Importo, Percentuale o Calcolato). Il valore lo calcola sempre il server."
                onChange={e => setField('formula', e.target.value)} />
            </FormSection>
          )}

          {(isNumeric || form.type === 'Testo' || form.type === 'Calcolato') && (
            <FormSection title="Validazione">
              {isNumeric && (
                <>
                  <TextField id="dfm-min" label="Minimo" type="number" value={form.min} onChange={e => setField('min', e.target.value)} />
                  <TextField id="dfm-max" label="Massimo" type="number" value={form.max} onChange={e => setField('max', e.target.value)} />
                </>
              )}
              {(isNumeric || form.type === 'Calcolato') && (
                <TextField id="dfm-dec" label="Decimali" type="number" min={0} max={6} value={form.decimals} onChange={e => setField('decimals', e.target.value)} />
              )}
              {form.type === 'Testo' && (
                <TextField id="dfm-ml" label="Lunghezza massima" type="number" min={1} max={4000} value={form.maxLength} onChange={e => setField('maxLength', e.target.value)} />
              )}
            </FormSection>
          )}

          {supportsDefault(form.type) && (
            <FormSection title="Valore predefinito" description="Compila da solo il campo quando si apre una nuova pratica (con il flag Pratica: campi condizionali acceso). Vuoto = nessuno.">
              {form.type === 'Casella' ? (
                <Select id="dfm-default" label="Valore predefinito" value={form.defaultText} onChange={e => setField('defaultText', e.target.value)}>
                  <option value="">Nessuno</option>
                  <option value="true">Spuntata</option>
                </Select>
              ) : form.type === 'Elenco' ? (
                <Select id="dfm-default" label="Valore predefinito" value={form.defaultText} onChange={e => setField('defaultText', e.target.value)}>
                  <option value="">Nessuno</option>
                  {form.optionsText.split('\n').map(o => o.trim()).filter(o => o !== '').map(o => <option key={o} value={o}>{o}</option>)}
                </Select>
              ) : (
                <TextField id="dfm-default" label="Valore predefinito" value={form.defaultText} maxLength={500}
                  type={form.type === 'Data' ? 'date' : 'text'}
                  supportingText={form.type === 'ElencoMultiplo' ? 'Più opzioni separate da punto e virgola.' : 'Deve rispettare le regole del campo.'}
                  onChange={e => setField('defaultText', e.target.value)} />
              )}
            </FormSection>
          )}

          <FormSection title="Condizioni di visualizzazione" columns={1}>
            {form.conditions.length === 0 && <div className="md-typescale-body-small text-muted">Nessuna: il campo e&apos; sempre visibile (se l&apos;ambito combacia). Un campo nascosto non e&apos; obbligatorio.</div>}
            {form.conditions.map((c, i) => (
              <FormGrid key={i}>
                <Select label="Campo" labelHidden value={c.field} onChange={e => updateCondition(i, { field: e.target.value })}>
                  <option value="">— scegli —</option>
                  {conditionFieldChoices.map(ch => <option key={ch.value} value={ch.value}>{ch.label}</option>)}
                </Select>
                <Select label="Operatore" labelHidden value={c.operator} onChange={e => updateCondition(i, { operator: e.target.value as ConditionOperator })}>
                  {CONDITION_OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
                <div className="md-span-full d-flex gap-2 align-items-start">
                  <TextField className="flex-grow-1" label="Valore" labelHidden value={c.value} placeholder="valore (true/false per una casella)" onChange={e => updateCondition(i, { value: e.target.value })} />
                  <IconButton icon="delete" aria-label="Rimuovi"
                    onClick={() => setField('conditions', form.conditions.filter((_, idx) => idx !== i))} />
                </div>
              </FormGrid>
            ))}
            <div>
              <Button variant="outlined" size="sm" disabled={form.conditions.length >= MAX_CONDITIONS}
                onClick={() => setField('conditions', [...form.conditions, { field: '', operator: 'eq', value: '' }])}>
                Aggiungi condizione
              </Button>
            </div>
          </FormSection>

          {!isContactForm && form.type !== 'Calcolato' && (
            <FormSection title="Origine del valore" columns={1}>
              <datalist id="dfm-question-list">
                {questions.map(q => <option key={q.code} value={q.code}>{`${q.label} (${q.formName})`}</option>)}
              </datalist>
              {form.sources.length === 0 && (
                <div className="md-typescale-body-small text-muted">
                  Nessuna: il campo si precompila dalla risposta con lo stesso codice. Con piu&apos; origini vale la prima
                  che da&apos; un valore valido; un valore gia&apos; presente nella pratica non si sovrascrive mai.
                  Il dato anagrafico non viene mai modificato dal campo.
                </div>
              )}
              {form.sources.map((src, i) => (
                <div key={i} className="d-flex flex-wrap gap-2 align-items-center">
                  <StatusPill tone="neutral">{i + 1}. {src.kind === 'question' ? 'Domanda' : 'Anagrafica'}</StatusPill>
                  {src.kind === 'question' ? (
                    <TextField className="flex-grow-1 md-u-minw-200" label="Codice della domanda" labelHidden list="dfm-question-list" maxLength={40}
                      placeholder="codice della domanda del questionario" value={src.code}
                      onChange={e => updateSource(i, { kind: 'question', code: e.target.value })} />
                  ) : (
                    <Select className="flex-grow-1 md-u-minw-200" label="Dato anagrafico" labelHidden value={src.attribute}
                      onChange={e => updateSource(i, { kind: 'customer', attribute: e.target.value })}>
                      <option value="">— scegli —</option>
                      {CUSTOMER_PREFILL_ATTRIBUTES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </Select>
                  )}
                  <div className="d-flex gap-1">
                    <IconButton icon="arrow_upward" aria-label="Sposta su" disabled={i === 0} onClick={() => moveSource(i, -1)} />
                    <IconButton icon="arrow_downward" aria-label="Sposta giu'" disabled={i === form.sources.length - 1} onClick={() => moveSource(i, 1)} />
                    <IconButton icon="delete" aria-label="Rimuovi"
                      onClick={() => setField('sources', form.sources.filter((_, idx) => idx !== i))} />
                  </div>
                </div>
              ))}
              <div className="d-flex flex-wrap gap-2">
                <Button variant="outlined" size="sm" disabled={form.sources.length >= MAX_PREFILL_SOURCES}
                  onClick={() => setField('sources', [...form.sources, { kind: 'question', code: '' }])}>
                  Aggiungi domanda
                </Button>
                <Button variant="outlined" size="sm" disabled={form.sources.length >= MAX_PREFILL_SOURCES}
                  onClick={() => setField('sources', [...form.sources, { kind: 'customer', attribute: '' }])}>
                  Aggiungi dato anagrafico
                </Button>
              </div>
            </FormSection>
          )}

          <FormSection title="Tracciabilità">
            <TextField id="dfm-ext" label="Mapping portale (solo etichetta)" value={form.externalMappingLabel} maxLength={100}
              supportingText="Informativa: non c'e' alcun collegamento reale con i portali degli operatori."
              onChange={e => setField('externalMappingLabel', e.target.value)} />
            <TextField id="dfm-reason" label="Motivazione della modifica" value={form.changeReason} maxLength={500} onChange={e => setField('changeReason', e.target.value)} />
          </FormSection>

          <FormSection title="Opzioni" columns={1}>
            <Checkbox id="dfm-required" label="Obbligatorio" checked={form.type === 'Calcolato' ? false : form.required}
              disabled={form.type === 'Calcolato'} onChange={e => setField('required', e.target.checked)} />
          </FormSection>
        </>
      </Dialog>

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleNewVersion() }}
        open={versionTarget !== null}
        onClose={() => setVersionTarget(null)}
        title={`Nuova versione di "${versionTarget?.label ?? ''}"`}
        supportingText={versionTarget ? (
          <>
            Crea la versione {versionTarget.version + 1} in bozza, copia dell&apos;attuale. Per attivarla bisogna
            prima archiviare la versione attiva con lo stesso ambito. I valori gia&apos; salvati restano alla
            versione con cui sono stati scritti.
          </>
        ) : undefined}
        actions={
          <>
            <Button variant="text" onClick={() => setVersionTarget(null)}>Annulla</Button>
            <Button type="submit" variant="text" >Crea versione</Button>
          </>
        }
      >
        {versionTarget && (
          <>
            {versionError && <Callout tone="error">{versionError}</Callout>}
            <FormGrid columns={1}>
              <TextField id="dfv-reason" label="Motivazione" value={versionReason} maxLength={500} onChange={e => setVersionReason(e.target.value)} />
            </FormGrid>
          </>
        )}
      </Dialog>
    </Page>
  )
}
